/** @module Upload queue for scans: keeps photos until the server has them.

IndexedDB is the preferred home, because it survives a reload. Safari on iOS fails it
in two ways that both cost photos: `open()` can stay pending for good, and a running
transaction can die with "UnknownError". Either way the queue falls back to memory and
scanning continues; the only thing lost is surviving a reload while offline.
*/

import { log } from "@/lib/client-log";
import { withTimeout } from "@/lib/timeout";

const DB_NAME = "pokescan-scans";
const STORE = "pending";
const VERSION = 1;

export type CaptureInfo = Record<string, number | string | boolean | null>;

export interface PendingScan {
  key: string;
  sessionId: string;
  photo: Blob;
  /** Where the person aimed, in pixels of the photo; the server treats it as a hint. */
  quad?: number[][];
  /** How the phone took it (trigger, sizes, settle time); stored with the scan. */
  capture?: CaptureInfo;
  createdAt: number;
  attempts: number;
}

/** How long the browser gets to open the database before we give up on it. */
const OPEN_TIMEOUT_MS = 2500;

let memory: PendingScan[] = [];
let useMemory = false;
let database: IDBDatabase | null = null;

function giveUpOnDatabase(reason: string): void {
  if (useMemory) return;
  useMemory = true;
  database = null;
  log("scan.queue_in_memory", { reason }, "warning");
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error("IndexedDB is not available"));
    };
    request.onblocked = () => {
      reject(new Error("IndexedDB is blocked by another tab"));
    };
  });
}

async function open(): Promise<IDBDatabase | null> {
  if (useMemory) return null;
  if (database) return database;
  try {
    database = await withTimeout(openDatabase(), OPEN_TIMEOUT_MS, "indexedDB.open");
    // A connection can be lost later; the next call then opens a new one.
    database.onclose = () => {
      database = null;
    };
    return database;
  } catch (cause) {
    giveUpOnDatabase(cause instanceof Error ? cause.name : "unknown");
    return null;
  }
}

/** Run one transaction; null means the database refused and memory has to take over. */
async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await open();
  if (db === null) return null;
  try {
    return await new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = run(transaction.objectStore(STORE));
      request.onsuccess = () => {
        resolve(request.result);
      };
      request.onerror = () => {
        reject(request.error ?? new Error("IndexedDB request failed"));
      };
      transaction.onabort = () => {
        reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
      };
    });
  } catch (cause) {
    // "UnknownError" mid transaction is a Safari speciality; a photo must not be lost.
    giveUpOnDatabase(cause instanceof Error ? cause.name : "unknown");
    return null;
  }
}

/** Put a photo in the queue; the key is also the Idempotency-Key of the upload. */
export async function enqueue(
  sessionId: string,
  photo: Blob,
  quad?: number[][],
  capture?: CaptureInfo,
): Promise<PendingScan> {
  const entry: PendingScan = {
    key: crypto.randomUUID(),
    sessionId,
    photo,
    quad,
    capture,
    createdAt: Date.now(),
    attempts: 0,
  };
  const stored = await withStore("readwrite", (store) => store.add(entry));
  if (stored === null) memory.push(entry);
  return entry;
}

export async function pending(): Promise<PendingScan[]> {
  const all = await withStore<unknown[]>(
    "readonly",
    (store) => store.getAll() as IDBRequest<unknown[]>,
  );
  const entries = all === null ? memory : (all as PendingScan[]);
  return [...entries].sort((a, b) => a.createdAt - b.createdAt);
}

export async function remove(key: string): Promise<void> {
  await withStore("readwrite", (store) => store.delete(key));
  memory = memory.filter((entry) => entry.key !== key);
}

export async function markAttempt(entry: PendingScan): Promise<void> {
  const next = { ...entry, attempts: entry.attempts + 1 };
  await withStore("readwrite", (store) => store.put(next));
  memory = memory.map((item) => (item.key === entry.key ? next : item));
}

export async function clear(): Promise<void> {
  await withStore("readwrite", (store) => store.clear());
  memory = [];
}

/** Test seam: forget that the database ever failed. */
export function resetQueue(): void {
  memory = [];
  useMemory = false;
  database = null;
}
